import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export function Hero() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
        }
      },
      { threshold: 0.1 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (logoContainerRef.current) {
      const rect = logoContainerRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  return (
    <section id='home' className="py-12  bg-white dark:bg-black dark:from-black dark:to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
          {/* Logo Side */}
          <div className="relative">
            <div
              ref={logoContainerRef}
              className="w-full h-[600px] relative cursor-none"
              onMouseMove={handleMouseMove}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              <img
                src={theme === 'dark' ? '/img/logo-large-bw-dark.svg' : '/img/logo-large-bw.svg'}
                alt="Ad Vitad Logo Black and White"
                className="w-full h-full object-contain animate-float"
              />
              
              <div
                className="absolute inset-0 transition-opacity duration-300"
                style={{
                  opacity: isHovering ? 1 : 0,
                  mask: isHovering
                    ? `radial-gradient(circle 100px at ${mousePosition.x}px ${mousePosition.y}px, white, transparent)`
                    : 'none',
                  WebkitMask: isHovering
                    ? `radial-gradient(circle 100px at ${mousePosition.x}px ${mousePosition.y}px, white, transparent)`
                    : 'none',
                }}
              >
                <img
                  src={theme === 'dark' ? '/img/logo-large-color-dark.svg' : '/img/logo-large-color.svg'}
                  alt="Ad Vitad Logo Colored"
                  className="w-full h-full object-contain animate-float"
                />
              </div>
            </div>
          </div>

          {/* Text Side */}
          <div className="text-center lg:text-left">
            <h1 ref={titleRef} className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white opacity-0 translate-y-4 transition-all duration-1000">
              {t('hero.title')}
            </h1>
            <p className="mt-6 mb-6 text-xl text-gray-600 dark:text-gray-300">
              {t('hero.subtitle')}
            </p>
            <div className="flex gap-4">
            <Link 
              to="/contact" 
              className="inline-block px-8 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              {t('hero.get_started')}
            </Link>
            <a 
              href="#expertise" 
              className="inline-block px-8 py-3 border border-gray-300 dark:border-gray-600 rounded-full 
                bg-transparent text-black hover:bg-blue-600 hover:text-white 
                dark:bg-white dark:text-black dark:hover:bg-blue-600 dark:hover:text-white 
                transition-colors"
            >
              {t('hero.learn_more')}
            </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}